import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Normalises the two viewport behaviours where iOS diverges from Android, so
 * both platforms render the same layout.
 *
 * 1. `--kb-inset` — how much of the screen the on-screen keyboard covers.
 *
 *    Android/Chrome shrinks the layout viewport when the keyboard opens, so a
 *    `position: fixed` composer is pushed up automatically. iOS does NOT: the
 *    layout viewport keeps its full height and the keyboard is drawn on top,
 *    leaving fixed-position inputs hidden behind it.
 *
 *    visualViewport reports the actually-visible region on both platforms, so
 *    (window height - visual viewport height - offsetTop) gives the covered
 *    height. On Android this stays ~0 because the layout already shrank, which
 *    is exactly what we want: one rule, no platform branch.
 *
 * Viewport events also fire while mobile browser chrome is animating. Updates
 * are batched and written only when the keyboard state actually changes so a
 * normal scroll gesture cannot invalidate the whole app's styles every frame.
 */
@Injectable({ providedIn: 'root' })
export class ViewportParityService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private visualViewport: VisualViewport | null = null;
  private readonly onViewportChange = () => this.scheduleUpdate();
  private started = false;
  private pendingFrame: number | null = null;
  private appliedKeyboardInset: number | null = null;
  private appliedKeyboardOpen: boolean | null = null;

  init(): void {
    // Guard against SSR/prerender, where there is no window.
    const win = this.document.defaultView;
    if (!win || this.started) return;
    this.started = true;

    this.visualViewport = win.visualViewport ?? null;

    // These fire on every keyboard open/close and on every pinch-zoom frame,
    // so keep them outside Angular to avoid triggering change detection.
    this.zone.runOutsideAngular(() => {
      this.visualViewport?.addEventListener('resize', this.onViewportChange);
      this.visualViewport?.addEventListener('scroll', this.onViewportChange);
      win.addEventListener('orientationchange', this.onViewportChange);
      this.scheduleUpdate();
    });
  }

  private scheduleUpdate(): void {
    const win = this.document.defaultView;
    if (!win || this.pendingFrame !== null) return;

    this.pendingFrame = win.requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.update();
    });
  }

  private update(): void {
    const win = this.document.defaultView;
    if (!win) return;

    const root = this.document.documentElement;
    const vv = this.visualViewport;

    // Height actually covered by the keyboard (0 when it is closed, and
    // effectively always 0 on Android because the layout viewport shrinks).
    let keyboardInset = 0;
    if (vv) {
      keyboardInset = Math.max(0, win.innerHeight - vv.height - vv.offsetTop);
    }

    // Ignore sub-pixel jitter and the small inset caused by pinch-zooming,
    // which would otherwise shift the layout while the user is just zooming.
    if (keyboardInset < 80) keyboardInset = 0;

    const roundedKeyboardInset = Math.round(keyboardInset);
    if (this.appliedKeyboardInset !== roundedKeyboardInset) {
      root.style.setProperty('--kb-inset', `${roundedKeyboardInset}px`);
      this.appliedKeyboardInset = roundedKeyboardInset;
    }

    const keyboardOpen = roundedKeyboardInset > 0;
    if (this.appliedKeyboardOpen !== keyboardOpen) {
      root.classList.toggle('keyboard-open', keyboardOpen);
      this.appliedKeyboardOpen = keyboardOpen;
    }
  }

  ngOnDestroy(): void {
    const win = this.document.defaultView;
    this.visualViewport?.removeEventListener('resize', this.onViewportChange);
    this.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    win?.removeEventListener('orientationchange', this.onViewportChange);
    if (this.pendingFrame !== null && win) {
      win.cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
  }
}
